import { convertFieldValueAvroToJson } from 'rpc-client-utils';
import { AutoPositionTypename, AutoRowOrColumnSizeTypename, BackgroundColorLayerTypename, BackgroundGradientLayerTypename, BackgroundImageLayerTypename, ContainerTypename, FitContentWidthTypename, FullContentWidthTypename, GridContainerLayoutTypename, MaxContentRowOrColumnSizeTypename, MaxContentWidthTypename, MinContentRowOrColumnSizeTypename, MinMaxRowOrColumnSizeTypename, ModuleTypename, SizedContentRowOrColumnSizeTypename, SizedWidthTypename, SplitGapTypename, StackContainerLayoutTypename, UnifiedGapTypename } from '../../__generated__/chirp/com/hubspot/content/grid/layouts/rpc/GridLayoutsRpc';
import { BACKGROUND_LAYER_TYPES, CONTAINER_TYPES, CONTENT_WIDTH_TYPES, GAP_TYPES, GRID_NODE_TYPES, POSITION_TYPES, ROW_COLUMN_SIZE_TYPES, WIDTH_TYPES } from './constants';
export const isRootNodeValue = rootNodeValue => {
  return rootNodeValue.type === GRID_NODE_TYPES.ROOT;
};
export const isSectionNodeValue = sectionNodeValue => {
  return sectionNodeValue.type === GRID_NODE_TYPES.SECTION;
};
export const isContainerNodeValue = containerNodeValue => {
  return containerNodeValue.type === GRID_NODE_TYPES.CONTAINER;
};
export const isModuleNodeValue = moduleNodeValue => {
  return moduleNodeValue.type === GRID_NODE_TYPES.MODULE;
};
export const isBackgroundLayerColor = backgroundLayer => {
  return backgroundLayer.type === BACKGROUND_LAYER_TYPES.COLOR || backgroundLayer.__typename === BackgroundColorLayerTypename;
};
export const isBackgroundLayerGradient = backgroundLayer => {
  return backgroundLayer.type === BACKGROUND_LAYER_TYPES.GRADIENT || backgroundLayer.__typename === BackgroundGradientLayerTypename;
};
export const isBackgroundLayerImage = backgroundLayer => {
  return backgroundLayer.type === BACKGROUND_LAYER_TYPES.IMAGE || backgroundLayer.__typename === BackgroundImageLayerTypename;
};
export const isUnifiedGap = gap => {
  return gap.type === GAP_TYPES.UNIFIED || gap.__typename === UnifiedGapTypename;
};
export const isSplitGap = gap => {
  return gap.type === GAP_TYPES.SPLIT || gap.__typename === SplitGapTypename;
};
export const isMaxContentWidth = width => {
  return width.type === CONTENT_WIDTH_TYPES.MAX_WIDTH || width.__typename === MaxContentWidthTypename;
};
export const isFullContentWidth = width => {
  return width.type === CONTENT_WIDTH_TYPES.FULL_WIDTH || width.__typename === FullContentWidthTypename;
};
export const isAutoPosition = position => {
  return position.type === POSITION_TYPES.AUTO || position.__typename === AutoPositionTypename;
};
export const isMinMaxRowOrColumnSize = size => {
  return size.type === ROW_COLUMN_SIZE_TYPES.MINMAX || size.__typename === MinMaxRowOrColumnSizeTypename;
};
export const isAutoRowOrColumnSize = size => {
  return size.type === ROW_COLUMN_SIZE_TYPES.AUTO || size.__typename === AutoRowOrColumnSizeTypename;
};
export const isMaxContentRowOrColumnSize = size => {
  return size.type === ROW_COLUMN_SIZE_TYPES.MAX_CONTENT || size.__typename === MaxContentRowOrColumnSizeTypename;
};
export const isMinContentRowOrColumnSize = size => {
  return size.type === ROW_COLUMN_SIZE_TYPES.MIN_CONTENT || size.__typename === MinContentRowOrColumnSizeTypename;
};
export const isSizedContentRowOrColumnSize = size => {
  return size.type === ROW_COLUMN_SIZE_TYPES.SIZED || size.__typename === SizedContentRowOrColumnSizeTypename;
};
export const isGridContainerLayout = layout => {
  return layout.type === CONTAINER_TYPES.GRID || layout.__typename === GridContainerLayoutTypename;
};
export const isStackContainerLayout = layout => {
  return layout.type === CONTAINER_TYPES.STACK || layout.__typename === StackContainerLayoutTypename;
};
export const isSizedWidth = width => {
  return width.type === WIDTH_TYPES.SIZED || width.__typename === SizedWidthTypename;
};
export const isFitContentWidth = width => {
  return width.type === WIDTH_TYPES.FIT_CONTENT || width.__typename === FitContentWidthTypename;
};
export const isContainer = gridContent => {
  return gridContent.type === GRID_NODE_TYPES.CONTAINER || gridContent.__typename === ContainerTypename;
};
export const isModule = gridContent => {
  return gridContent.type === GRID_NODE_TYPES.MODULE || gridContent.__typename === ModuleTypename;
};
export const isSection = section => {
  return section.type === GRID_NODE_TYPES.SECTION;
};
/**
 * When working with some intermediary service that consumes the rpc client but
 * doesn't itself serve an rpc client, that means our "field value avro" was
 * instead serialized by jackson instead of chirp, meaning that we'll need to
 * handle discriminating against `@type` instead of `__typename`.
 * @param avroValue
 * @returns
 */
export const convertFieldValueAvroWithoutTypenameToJson = avroValue => {
  const value = convertFieldValueAvroToJson(avroValue);
  if (value) return value;
  const typedValue = avroValue;
  switch (typedValue['@type']) {
    case 'null':
      return null;
    case 'string':
    case 'integer':
    case 'long':
    case 'double':
    case 'boolean':
      return typedValue.value;
    case 'list':
      {
        const listValue = typedValue;
        return listValue.value.map(item => convertFieldValueAvroWithoutTypenameToJson(item));
      }
    case 'map':
      {
        const jsonObject = {};
        const mapValue = typedValue.value;
        for (const key in mapValue) {
          if (Object.prototype.hasOwnProperty.call(mapValue, key)) {
            jsonObject[key] = convertFieldValueAvroWithoutTypenameToJson(mapValue[key]);
          }
        }
        return jsonObject;
      }
    default:
      return null;
  }
};

/**
 * Combines both the RPC __typename and the jackson @type field discriminators
 */

/**
 * Converts plain JSON format to our "special" FakeFieldValueAvro format that
 * includes both the RPC __typename and the jackson @type field discriminators
 */
export function convertJsonToFieldValueAvroWithoutTypename(jsonValue) {
  if (jsonValue === null || jsonValue === undefined) {
    return {
      __typename: 'com.hubspot.chirp.ext.models.NullFieldValue',
      '@type': 'null'
    };
  }
  const type = typeof jsonValue;
  if (type === 'string') {
    return {
      __typename: 'com.hubspot.chirp.ext.models.StringFieldValue',
      '@type': 'string',
      value: jsonValue
    };
  }
  if (type === 'number') {
    // Determine if the number is an integer or floating point
    if (Number.isInteger(jsonValue) && jsonValue <= 2147483647 && jsonValue >= -2147483648) {
      return {
        __typename: 'com.hubspot.chirp.ext.models.IntFieldValue',
        '@type': 'integer',
        value: jsonValue
      };
    } else if (Number.isInteger(jsonValue)) {
      return {
        __typename: 'com.hubspot.chirp.ext.models.LongFieldValue',
        '@type': 'long',
        value: jsonValue
      };
    } else {
      return {
        __typename: 'com.hubspot.chirp.ext.models.DoubleFieldValue',
        '@type': 'double',
        value: jsonValue
      };
    }
  }
  if (type === 'boolean') {
    return {
      __typename: 'com.hubspot.chirp.ext.models.BooleanFieldValue',
      '@type': 'boolean',
      value: jsonValue
    };
  }
  if (Array.isArray(jsonValue)) {
    return {
      __typename: 'com.hubspot.chirp.ext.models.ListFieldValue',
      '@type': 'list',
      value: jsonValue.map(item => convertJsonToFieldValueAvroWithoutTypename(item))
    };
  }
  if (type === 'object') {
    const avroObject = {};
    const objValue = jsonValue;
    for (const key in objValue) {
      if (Object.prototype.hasOwnProperty.call(objValue, key)) {
        avroObject[key] = convertJsonToFieldValueAvroWithoutTypename(objValue[key]);
      }
    }
    return {
      __typename: 'com.hubspot.chirp.ext.models.MapFieldValue',
      '@type': 'map',
      value: avroObject
    };
  }

  // Default fallback for unsupported types
  return {
    __typename: 'com.hubspot.chirp.ext.models.NullFieldValue',
    '@type': 'null'
  };
}